import React, { useState, useEffect } from "react";
import { useNotifications } from "../../../hooks/useNotifications";
import { useNotificationUpload } from "../../../hooks/useNotificationUpload";
import { useNotificationDelete } from "../../../hooks/useNotificationDelete";

interface NotificacoesTabProps {
  // Props específicas se necessário
}

const NotificacoesTab: React.FC<NotificacoesTabProps> = () => {
  // Estados locais para a aba de notificações
  const [notificationForm, setNotificationForm] = useState({
    title: "",
    message: "",
  });
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [hasImage, setHasImage] = useState<boolean>(false);
  const [currentNotificationPage, setCurrentNotificationPage] = useState(1);
  const notificationPageSize = 10;

  // Hooks específicos para notificações
  const { fetchNotifications, notificationState } = useNotifications();
  const { createNotificationWithImage, uploadState, resetUploadState } = useNotificationUpload();
  const { deleteNotification, deleteState } = useNotificationDelete();

  // Buscar notificações ao montar o componente
  useEffect(() => {
    fetchNotifications();
  }, []);

  // Funções específicas para notificações
  const handleImageFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setImageFile(file);
      setHasImage(true);
    }
  };

  const handleCreateNotification = async () => {
    if (!notificationForm.title || !notificationForm.message) return;

    if (hasImage && !imageFile) {
      alert("Selecione uma imagem para a notificação");
      return;
    }

    const notificationData = {
      title: notificationForm.title,
      message: notificationForm.message,
      image: hasImage && imageFile !== null, // Flag: só tem imagem se marcado E tiver arquivo
    };

    const result = await createNotificationWithImage(
      notificationData,
      imageFile
    );

    if (result) {
      setNotificationForm({ title: "", message: "" });
      setImageFile(null);
      setHasImage(false);
      resetUploadState();
      fetchNotifications();
    }
  };

  const handleDeleteNotification = async (notificationId: string) => {
    if (window.confirm("Tem certeza que deseja deletar esta notificação?")) {
      const success = await deleteNotification(notificationId);
      if (success) {
        fetchNotifications();

        // Ajustar página atual se necessário
        const newTotalPages = Math.ceil(
          (notificationState.notifications.length - 1) / notificationPageSize
        );
        if (currentNotificationPage > newTotalPages && newTotalPages > 0) {
          setCurrentNotificationPage(newTotalPages);
        }
      }
    }
  };

  // Paginação
  const totalNotificationPages = Math.ceil(
    notificationState.notifications.length / notificationPageSize
  );
  const getPaginatedNotifications = () => {
    const startIndex = (currentNotificationPage - 1) * notificationPageSize;
    const endIndex = startIndex + notificationPageSize;
    return notificationState.notifications.slice(startIndex, endIndex);
  };

  // Loading state
  if (notificationState.isLoading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <span>Carregando notificações...</span>
      </div>
    );
  }

  // Error state
  if (notificationState.error) {
    return <div className="error-message">{notificationState.error}</div>;
  }

  return (
    <div className="notifications-container">
      {/* Formulário para criar notificação */}
      <div className="notification-form-section">
        <h3>🔔 Criar Nova Notificação</h3>
        <div className="form-group">
          <input
            type="text"
            placeholder="Título da notificação"
            value={notificationForm.title}
            onChange={(e) =>
              setNotificationForm((prev) => ({
                ...prev,
                title: e.target.value,
              }))
            }
          />
        </div>
        <div className="form-group">
          <textarea
            placeholder="Mensagem da notificação (pode conter HTML)"
            value={notificationForm.message}
            onChange={(e) =>
              setNotificationForm((prev) => ({
                ...prev,
                message: e.target.value,
              }))
            }
            rows={6}
          />
          <small style={{ color: "rgba(255, 255, 255, 0.6)", display: "block", marginTop: "4px" }}>
            💡 Você pode usar HTML na mensagem (ex: &lt;strong&gt;, &lt;br&gt;, &lt;a&gt;, etc)
          </small>
        </div>
        <div className="form-group">
          <label>
            <input
              type="checkbox"
              checked={hasImage}
              onChange={(e) => {
                setHasImage(e.target.checked);
                if (!e.target.checked) {
                  setImageFile(null);
                }
              }}
              style={{ marginRight: "8px" }}
            />
            Notificação terá imagem
          </label>
          {hasImage && (
            <>
              <input
                type="file"
                accept="image/*"
                onChange={handleImageFileSelect}
                className="image-upload-input"
                style={{ marginTop: "8px" }}
              />
              {imageFile && (
                <div className="thumbnail-preview" style={{ marginTop: "8px" }}>
                  <img
                    src={URL.createObjectURL(imageFile)}
                    alt="Preview"
                    className="thumbnail-preview-image"
                    style={{ maxWidth: "200px", maxHeight: "200px" }}
                  />
                  <span className="thumbnail-file-name">{imageFile.name}</span>
                </div>
              )}
            </>
          )}
        </div>
        <button
          className="create-notification-button"
          onClick={handleCreateNotification}
          disabled={
            !notificationForm.title ||
            !notificationForm.message ||
            uploadState.isUploading ||
            (hasImage && !imageFile)
          }
        >
          {uploadState.isUploading ? "Criando..." : "Criar Notificação"}
        </button>

        {uploadState.error && (
          <div className="error-message">❌ {uploadState.error}</div>
        )}

        {uploadState.success && (
          <div className="success-message">
            ✅ Notificação criada com sucesso!
          </div>
        )}
      </div>

      {/* Lista de Notificações */}
      <div className="notifications-list-section">
        <h3>📋 Notificações</h3>

        {notificationState.notifications.length === 0 ? (
          <p>Nenhuma notificação encontrada.</p>
        ) : (
          <div className="notifications-table-container">
            <table className="notifications-table">
              <thead>
                <tr>
                  <th>Título</th>
                  <th>Mensagem</th>
                  <th>Imagem</th>
                  <th>Data</th>
                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                {getPaginatedNotifications().map((notification) => (
                  <tr key={notification.id} className="notification-row">
                    <td className="notification-title">{notification.title}</td>
                    <td className="notificatsion-mesage">
                      {(() => {
                        const textOnly = notification.message.replace(/<[^>]*>/g, "");
                        return textOnly.length > 50 
                          ? textOnly.substring(0, 50) + "..." 
                          : textOnly;
                      })()}
                    </td>
                    <td className="notification-image-cell">
                      {notification.imageUrl ? (
                        <img
                          src={notification.imageUrl}
                          alt="Preview"
                          style={{ maxWidth: "60px", maxHeight: "60px", borderRadius: "4px" }}
                        />
                      ) : (
                        <span style={{ color: "rgba(255, 255, 255, 0.5)" }}>—</span>
                      )}
                    </td>
                    <td className="notification-date">
                      {new Date(notification.createdAt).toLocaleDateString(
                        "pt-BR"
                      )}
                    </td>
                    <td className="notification-actions">
                      <button
                        className="delete-button"
                        onClick={() =>
                          handleDeleteNotification(notification.id)
                        }
                        disabled={deleteState.isLoading}
                      >
                        🗑️ Deletar
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Paginação */}
            {totalNotificationPages > 1 && (
              <div className="notifications-pagination">
                <button
                  onClick={() =>
                    setCurrentNotificationPage((prev) => Math.max(prev - 1, 1))
                  }
                  disabled={currentNotificationPage === 1}
                  className="pagination-button"
                >
                  ← Anterior
                </button>
                <span className="pagination-info">
                  Página {currentNotificationPage} de {totalNotificationPages}
                </span>
                <button
                  onClick={() =>
                    setCurrentNotificationPage((prev) =>
                      Math.min(prev + 1, totalNotificationPages)
                    )
                  }
                  disabled={currentNotificationPage === totalNotificationPages}
                  className="pagination-button"
                >
                  Próximo →
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default NotificacoesTab;
